import ReactMarkdown from "react-markdown";
import { cn } from "@githits/shared";
import type { ReplayPhase, SearchQuery } from "../data/types";

interface QueryThreadProps {
  query: string;
  phase: ReplayPhase;
  visibleSearchQueries: SearchQuery[];
  exampleTitle: string;
  explanation: string;
}

export default function QueryThread({
  query,
  phase,
  visibleSearchQueries,
  exampleTitle,
  explanation,
}: QueryThreadProps) {
  const showResponse =
    phase !== "idle" && phase !== "typing" && phase !== "submitting";
  const isSearching = phase === "searching";
  const isWorking =
    phase === "searching" || phase === "analyzing" || phase === "generating";

  return (
    <div className="ghw-flex ghw-flex-col ghw-h-full">
      <div className="ghw-px-4 ghw-py-3 ghw-border-b ghw-border-border ghw-shrink-0">
        <h3 className="ghw-text-sm ghw-font-medium ghw-text-muted-foreground">
          Query Thread
        </h3>
      </div>

      <div className="ghw-flex-1 ghw-overflow-y-auto ghw-p-4 ghw-space-y-4 replay-dark-scrollbar">
        {/* User question bubble — right-aligned with primary left border */}
        {query && (
          <div className="ghw-flex ghw-justify-end">
            <div className="ghw-max-w-[85%] ghw-border-l-2 ghw-border-primary ghw-bg-secondary ghw-px-4 ghw-py-3">
              <p className="ghw-text-sm ghw-text-foreground">{query}</p>
            </div>
          </div>
        )}

        {/* GitHits response */}
        {showResponse && (
          <div className="ghw-flex ghw-justify-start">
            <div className="ghw-max-w-[90%] ghw-bg-muted/50 ghw-rounded-lg ghw-px-4 ghw-py-3 ghw-space-y-2">
              {/* Spinner while working */}
              {isWorking && (
                <div className="ghw-flex ghw-items-center ghw-gap-2">
                  <svg
                    className="ghw-w-4 ghw-h-4 ghw-animate-spin ghw-text-primary"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <circle
                      className="ghw-opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="ghw-opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  <span className="ghw-text-xs ghw-text-muted-foreground">
                    Working...
                  </span>
                </div>
              )}

              {/* Search queries — only visible during searching phase */}
              {isSearching && visibleSearchQueries.length > 0 && (
                <p className="ghw-text-xs ghw-leading-relaxed ghw-text-primary/80 ghw-italic">
                  Searching for:{" "}
                  {visibleSearchQueries.map((q) => q.query).join(", ")}
                </p>
              )}

              {/* Placeholder for explanation when generating/done */}
              {(phase === "analyzing" || phase === "generating") && (
                <p className="ghw-text-xs ghw-text-muted-foreground">
                  Analyzing sources and generating example...
                </p>
              )}

              {phase === "done" && (
                <div className="ghw-space-y-2">
                  {exampleTitle && (
                    <div className="ghw-pb-2 ghw-mb-1 ghw-border-b ghw-border-border">
                      <h4 className="ghw-text-base ghw-font-lexend ghw-font-semibold ghw-text-foreground">
                        {exampleTitle}
                      </h4>
                    </div>
                  )}
                  {explanation && (
                    <div className="ghw-prose ghw-prose-sm ghw-prose-invert ghw-max-w-none prose-headings:ghw-text-foreground prose-headings:ghw-text-sm prose-headings:ghw-font-semibold prose-p:ghw-text-foreground/80 prose-p:ghw-text-sm prose-p:ghw-leading-relaxed prose-li:ghw-text-foreground/80 prose-li:ghw-text-sm prose-a:ghw-text-primary prose-a:ghw-no-underline hover:prose-a:ghw-underline">
                      <ReactMarkdown>{explanation}</ReactMarkdown>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Disabled chat prompt at bottom */}
      <div className="ghw-px-4 ghw-py-3 ghw-border-t ghw-border-border ghw-shrink-0">
        <div
          className={cn(
            "ghw-flex ghw-items-center ghw-rounded-lg ghw-border ghw-border-border ghw-bg-muted/30 ghw-px-3 ghw-py-2",
            "ghw-opacity-50 ghw-cursor-not-allowed"
          )}
        >
          <span className="ghw-text-xs ghw-text-muted-foreground">
            Refine your query
          </span>
        </div>
      </div>
    </div>
  );
}
